import { COLOSSUS_SLAYER_FEATURE_ID } from '../../../../shared/rules/subclasses/ranger/huntersPrey.mjs';

const LABEL = 'Colossus Slayer';

const ineligible = (reasonCode) => ({ status: 'ineligible', featureId: COLOSSUS_SLAYER_FEATURE_ID, featureLabel: LABEL, reasonCode });

const components = () => [{
  id: COLOSSUS_SLAYER_FEATURE_ID,
  label: LABEL,
  source: { type: 'feature', featureId: COLOSSUS_SLAYER_FEATURE_ID },
  dice: { count: 1, sides: 8 },
  flat: 0,
  damageType: { mode: 'inherit' },
  critical: { dice: 'double' },
}];

const usageClaim = (context) => ({
  campaignId: context.combat.campaignId,
  combatantId: context.attacker.combatantId,
  featureId: COLOSSUS_SLAYER_FEATURE_ID,
  turnSequence: context.combat.turnSequence,
  resolutionId: context.resolutionId,
});

/** True/false when the target's HP is known (current below maximum), otherwise 'unknown'. */
export const isTargetMissingHitPoints = (target) => {
  const current = Number(target?.currentHp);
  const maximum = Number(target?.maxHp ?? target?.hitPoints);
  if (target?.currentHp == null || !Number.isFinite(current) || !Number.isFinite(maximum) || maximum <= 0) return 'unknown';
  return current < maximum;
};

/**
 * 2024 Colossus Slayer: a weapon hit deals an extra 1d8 to a creature that is missing any Hit Points,
 * once per turn. Not optional, so a known-hurt target applies it automatically; unknown HP asks the player.
 */
export const evaluateColossusSlayer = ({ context }) => {
  if (context?.roll?.hit === false) return ineligible('not-a-hit');
  if (!context?.attack?.isWeaponAttack) return ineligible('not-weapon-attack');
  if (!context?.combat?.campaignId || !Number.isInteger(context?.combat?.turnSequence) || !context?.combat?.activeCombatantId
    || !context?.combat?.participantIds?.includes(context?.attacker?.combatantId)) return ineligible('no-authoritative-turn');
  const previous = context.combat.turnFeatureUsage?.[context.attacker.combatantId]?.[COLOSSUS_SLAYER_FEATURE_ID];
  if (previous?.turnSequence === context.combat.turnSequence && previous.resolutionId !== context.resolutionId) return ineligible('already-used-this-turn');
  const missing = isTargetMissingHitPoints(context.target?.entity);
  if (missing === false) return ineligible('target-unhurt');
  const result = { featureId: COLOSSUS_SLAYER_FEATURE_ID, featureLabel: LABEL, components: components(), claims: [usageClaim(context)] };
  if (missing === true) return { status: 'automatic', reasonCode: 'target-missing-hit-points', ...result };
  return {
    status: 'confirmation-required', reasonCode: 'target-hit-points-unknown', ...result,
    decision: {
      id: `${context.resolutionId}:${COLOSSUS_SLAYER_FEATURE_ID}`, kind: 'confirm-feature', featureId: COLOSSUS_SLAYER_FEATURE_ID,
      resolutionId: context.resolutionId, title: LABEL, label: LABEL,
      prompt: 'Is the target missing any of its Hit Points? Colossus Slayer adds 1d8 once per turn.',
      choices: [{ id: 'apply', label: 'Yes, it is hurt' }, { id: 'skip', label: 'No, full Hit Points' }],
    },
  };
};
